import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MaterialIcon } from '../components/ui/MaterialIcon';
import { Logo } from '../components/obros/Logo';

const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M23.52 12.27c0-.85-.08-1.67-.22-2.45H12v4.64h6.47a5.53 5.53 0 0 1-2.4 3.63v3.01h3.87c2.27-2.09 3.58-5.17 3.58-8.83Z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.9l-3.87-3.01c-1.08.72-2.46 1.15-4.07 1.15-3.13 0-5.78-2.11-6.73-4.96H1.27v3.11A12 12 0 0 0 12 24Z" />
    <path fill="#FBBC05" d="M5.27 14.28A7.2 7.2 0 0 1 4.89 12c0-.79.14-1.56.38-2.28V6.61H1.27A12 12 0 0 0 0 12c0 1.94.46 3.77 1.27 5.39l4-3.11Z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.44-3.44C17.95 1.19 15.23 0 12 0 7.31 0 3.26 2.69 1.27 6.61l4 3.11C6.22 6.86 8.87 4.75 12 4.75Z" />
  </svg>
);

// Material Symbols no tiene ícono de Apple — se calca a mano (glifo de manzana estándar).
const AppleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.37 1.43c.09 1.02-.3 2.02-.95 2.77-.68.78-1.8 1.4-2.87 1.31-.12-1 .36-2.05 1-2.76.7-.8 1.9-1.4 2.82-1.32ZM20.5 17.07c-.5 1.16-.74 1.68-1.39 2.7-.9 1.42-2.18 3.2-3.76 3.21-1.4.02-1.76-.91-3.66-.9-1.9.01-2.3.92-3.7.9-1.58-.02-2.8-1.63-3.7-3.04-2.54-3.96-2.8-8.6-1.24-11.07.11-.19 1.84-3 3.73-3.02 1.45-.02 2.42.98 3.63.98 1.21 0 2.07-.98 3.58-.98 1.26 0 2.4.63 3.36 1.7-2.96 1.62-2.48 5.86.15 6.52Z" />
  </svg>
);
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuthStore } from '../stores/authStore';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isLoading } = useAuthStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al iniciar sesión');
    }
  };

  return (
    <div className="w-full max-w-md px-4">
      <div className="text-center mb-8">
        <Logo className="h-10 mx-auto mb-5" />
        <h1 className="text-2xl font-bold text-onSurface">Bienvenido a Obros</h1>
        <p className="text-onSurface-mute mt-1">Conectamos oficios con oportunidades</p>
      </div>

      <div className="bg-surface rounded-[24px] shadow-xl border border-outlineVarWarm p-8">
        <h2 className="text-lg font-semibold text-onSurface mb-6">Iniciar sesión</h2>

        {error && (
          <div className="mb-4 p-3 bg-danger-50 text-danger-700 rounded-lg text-sm">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            pill
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            leftIcon={<MaterialIcon name="mail" size={18} />}
            placeholder="tu@email.com"
            required
          />

          <Input
            pill
            label="Contraseña"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            leftIcon={<MaterialIcon name="lock" size={18} />}
            rightIcon={
              <button type="button" onClick={() => setShowPassword(!showPassword)}>
                <MaterialIcon name={showPassword ? 'visibility_off' : 'visibility'} size={18} className="text-onSurface-mute" />
              </button>
            }
            placeholder="••••••••"
            required
          />

          <div className="flex justify-end text-sm">
            <Link to="/forgot-password" className="text-primary-600 hover:text-primary-700 font-medium">
              ¿Olvidaste tu contraseña?
            </Link>
          </div>

          <Button type="submit" variant="filled" pill size="xl" className="w-full" isLoading={isLoading}>
            Iniciar sesión
          </Button>
        </form>

        {/* Calca el divider + botones stub de login_screen.dart (sin integración real todavía) */}
        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-outlineVarWarm" />
          <span className="text-xs text-onSurface-mute">o continuá con</span>
          <div className="flex-1 h-px bg-outlineVarWarm" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className="flex items-center justify-center gap-2 py-2.5 rounded-full border border-outlineVarWarm text-sm font-medium text-onSurface hover:bg-surface-dim transition-colors"
          >
            <GoogleIcon className="w-4 h-4" /> Google
          </button>
          <button
            type="button"
            className="flex items-center justify-center gap-2 py-2.5 rounded-full border border-outlineVarWarm text-sm font-medium text-onSurface hover:bg-surface-dim transition-colors"
          >
            <AppleIcon className="w-[18px] h-[18px]" /> Apple
          </button>
        </div>

        <div className="mt-6 text-center text-sm text-onSurface-mute">
          ¿No tenés cuenta?{' '}
          <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
            Registrate gratis
          </Link>
        </div>
      </div>
    </div>
  );
};
